import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    CRM_ENABLE_TO_AUTO_OPEN_KEY,
    getDefaultExtensionSettings,
    GOOGLE_WORKSPACE_CHILDREN,
    type CrmEnableKey,
    type GoogleWorkspaceChildDef,
} from "../constants/extensionSettingsConfig";
import type { OverviewWidgetId, OverviewWidgetItem } from "../constants/overviewWidgetsConfig";
import type { ExtensionSettings, ExtensionSettingsFieldKey } from "../types/types";

const isCrmEnableKey = (key: ExtensionSettingsFieldKey): key is CrmEnableKey => key in CRM_ENABLE_TO_AUTO_OPEN_KEY;

const widgetsAreEqual = (a: OverviewWidgetItem[], b: OverviewWidgetItem[]): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    return a.every(({ id, isChecked }, i) => id === b[i].id && isChecked === b[i].isChecked);
};

const settingsAreEqual = (a: ExtensionSettings, b: ExtensionSettings): boolean => {
    const boolKeys = Object.keys(a).filter((k) => k !== "overviewWidgets") as ExtensionSettingsFieldKey[];
    return boolKeys.every((key) => a[key] === b[key]) && widgetsAreEqual(a.overviewWidgets, b.overviewWidgets);
};

const anyGoogleWorkspaceChildEnabled = (s: ExtensionSettings): boolean =>
    GOOGLE_WORKSPACE_CHILDREN.some(({ key }) => s[key]);

type UseExtensionSettingsOptions = {
    initialSettings?: ExtensionSettings;
    onSave?: (settings: ExtensionSettings, options: { onSuccess: () => void }) => void;
};

type UseExtensionSettingsResult = {
    settings: ExtensionSettings;
    hasChanges: boolean;
    setBooleanField: (key: ExtensionSettingsFieldKey, value: boolean) => void;
    toggleField: (key: ExtensionSettingsFieldKey) => void;
    toggleLinkedin: () => void;
    toggleCrmCard: (key: ExtensionSettingsFieldKey) => void;
    toggleGoogleWorkspace: () => void;
    toggleGoogleWorkspaceChild: (key: GoogleWorkspaceChildDef["key"]) => void;
    toggleAutoOpenLinkedin: () => void;
    reorderWidgets: (fromIndex: number, toIndex: number) => void;
    toggleWidget: (id: OverviewWidgetId) => void;
    handleCancel: () => void;
    handleSave: () => void;
};

export const useExtensionSettings = (options: UseExtensionSettingsOptions = {}): UseExtensionSettingsResult => {
    const { initialSettings, onSave } = options;

    const defaultSettings = useMemo(() => getDefaultExtensionSettings(), []);
    const resolvedSettings = initialSettings ?? defaultSettings;

    const [saved, setSaved] = useState<ExtensionSettings>(resolvedSettings);
    const [draft, setDraft] = useState<ExtensionSettings>(resolvedSettings);
    const initializedRef = useRef(false);

    useEffect(() => {
        if (!initializedRef.current && initialSettings != null) {
            initializedRef.current = true;
            setSaved(initialSettings);
            setDraft(initialSettings);
        }
    }, [initialSettings]);

    const hasChanges = useMemo(() => !settingsAreEqual(saved, draft), [saved, draft]);

    const setBooleanField = useCallback((key: ExtensionSettingsFieldKey, value: boolean) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    }, []);

    const toggleField = useCallback((key: ExtensionSettingsFieldKey) => {
        setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const toggleEnableWithAutoOpen = useCallback((enableKey: "enableLinkedin" | CrmEnableKey) => {
        setDraft((prev) => {
            const nextEnabled = !prev[enableKey];
            const autoOpenKey = enableKey === "enableLinkedin" ? "autoOpenLinkedin" : CRM_ENABLE_TO_AUTO_OPEN_KEY[enableKey];
            return {
                ...prev,
                [enableKey]: nextEnabled,
                [autoOpenKey]: nextEnabled,
            };
        });
    }, []);

    const toggleLinkedin = useCallback(() => {
        toggleEnableWithAutoOpen("enableLinkedin");
    }, [toggleEnableWithAutoOpen]);

    const toggleCrmCard = useCallback(
        (key: ExtensionSettingsFieldKey) => {
            if (isCrmEnableKey(key)) {
                toggleEnableWithAutoOpen(key);
                return;
            }
            toggleField(key);
        },
        [toggleEnableWithAutoOpen, toggleField],
    );

    const toggleGoogleWorkspace = useCallback(() => {
        setDraft((prev) => {
            const next = !prev.enableGoogleWorkspace;
            return {
                ...prev,
                enableGoogleWorkspace: next,
                enableGmail: next,
                enableGoogleCalendar: next,
                enableGoogleSheets: next,
            };
        });
    }, []);

    const toggleGoogleWorkspaceChild = useCallback((key: GoogleWorkspaceChildDef["key"]) => {
        setDraft((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            return {
                ...next,
                enableGoogleWorkspace: anyGoogleWorkspaceChildEnabled(next),
            };
        });
    }, []);

    const toggleAutoOpenLinkedin = useCallback(() => {
        setDraft((prev) => ({
            ...prev,
            autoOpenLinkedin: !prev.autoOpenLinkedin,
        }));
    }, []);

    const reorderWidgets = useCallback((fromIndex: number, toIndex: number) => {
        setDraft((prev) => {
            const next = [...prev.overviewWidgets];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return { ...prev, overviewWidgets: next };
        });
    }, []);

    const toggleWidget = useCallback((id: OverviewWidgetId) => {
        setDraft((prev) => ({
            ...prev,
            overviewWidgets: prev.overviewWidgets.map((w) => (w.id === id ? { ...w, isChecked: !w.isChecked } : w)),
        }));
    }, []);

    const handleCancel = useCallback(() => {
        setDraft(saved);
    }, [saved]);

    const handleSave = useCallback(() => {
        if (onSave) {
            onSave(draft, {
                onSuccess() {
                    setSaved(draft);
                },
            });
        }
    }, [draft, onSave]);

    return {
        settings: draft,
        hasChanges,
        setBooleanField,
        toggleField,
        toggleLinkedin,
        toggleCrmCard,
        toggleGoogleWorkspace,
        toggleGoogleWorkspaceChild,
        toggleAutoOpenLinkedin,
        reorderWidgets,
        toggleWidget,
        handleCancel,
        handleSave,
    };
};
